import React from 'react';

function Bar({ label, value, max, unit, color }) {
  const pct = Math.min(100, (Math.abs(value) / max) * 100);
  const isNeg = value < 0;
  return (
    <div style={s.barRow}>
      <span style={s.barLabel}>{label}</span>
      <div style={s.barTrack}>
        <div style={s.barCenter} />
        <div style={{
          ...s.barFill,
          width       : `${pct / 2}%`,
          background  : color,
          left        : isNeg ? `${50 - pct / 2}%` : '50%',
          boxShadow   : `0 0 6px ${color}66`,
        }} />
      </div>
      <span style={{ ...s.barVal, color }}>{value?.toFixed(2)}</span>
      <span style={s.barUnit}>{unit}</span>
    </div>
  );
}

export default function MotionPanel({ motion }) {
  return (
    <div style={s.card}>
      <div style={s.header}>
        <div style={s.titleRow}>
          <div style={s.iconWrap}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
          </div>
          <span style={s.title}>IMU · Motion Detail</span>
        </div>
        <span style={{
          ...s.motionBadge,
          background  : motion.motion ? '#eff6ff' : '#f3f4f6',
          color       : motion.motion ? '#2563eb' : '#6b7280',
          border      : `1px solid ${motion.motion ? '#bfdbfe' : '#e5e7eb'}`,
        }}>
          {motion.motion ? '⟳ High Motion' : '● Stable'}
        </span>
      </div>

      <div style={s.groups}>
        {/* Accelerometer */}
        <div style={s.group}>
          <span style={s.groupLabel}>Accelerometer (m/s²)</span>
          <Bar label="AX" value={motion.ax} max={20} unit="m/s²" color="#6366f1" />
          <Bar label="AY" value={motion.ay} max={20} unit="m/s²" color="#8b5cf6" />
          <Bar label="AZ" value={motion.az} max={20} unit="m/s²" color="#a78bfa" />
        </div>

        {/* Gyroscope */}
        <div style={s.group}>
          <span style={s.groupLabel}>Gyroscope (°/s)</span>
          <Bar label="GX" value={motion.gx} max={500} unit="°/s" color="#0ea5e9" />
          <Bar label="GY" value={motion.gy} max={500} unit="°/s" color="#38bdf8" />
          <Bar label="GZ" value={motion.gz} max={500} unit="°/s" color="#7dd3fc" />
        </div>
      </div>

      {/* Total accel */}
      <div style={s.totalRow}>
        <span style={s.totalLabel}>Total Acceleration</span>
        <div style={s.totalBar}>
          <div style={{
            ...s.totalFill,
            width: `${Math.min(100, (motion.accel / 30) * 100)}%`,
            background: motion.accel > 15
              ? 'linear-gradient(90deg, #3b82f6, #6366f1)'
              : 'linear-gradient(90deg, #10b981, #059669)',
          }} />
        </div>
        <span style={{ ...s.totalVal, color: motion.accel > 15 ? '#2563eb' : '#059669' }}>
          {motion.accel?.toFixed(2)} m/s²
        </span>
      </div>
    </div>
  );
}

const s = {
  card: {
    background      : 'rgba(255,255,255,0.72)',
    backdropFilter  : 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    borderRadius    : 20,
    border          : '1px solid rgba(255,255,255,0.9)',
    boxShadow       : '0 8px 32px rgba(99,102,241,0.08)',
    padding         : '20px 22px',
    display         : 'flex',
    flexDirection   : 'column',
    gap             : 16,
  },
  header   : { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  titleRow : { display: 'flex', alignItems: 'center', gap: 10 },
  iconWrap : {
    width: 30, height: 30, borderRadius: 8,
    background: 'linear-gradient(135deg, #eef2ff, #e0e7ff)',
    border: '1px solid #c7d2fe',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  title    : { fontSize: 14, fontWeight: 700, color: '#1e1b4b' },
  motionBadge: { fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 99 },
  groups   : { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  group    : { display: 'flex', flexDirection: 'column', gap: 8 },
  groupLabel: { fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 },
  barRow   : { display: 'flex', alignItems: 'center', gap: 8 },
  barLabel : { fontSize: 11, fontWeight: 700, color: '#6b7280', width: 22, flexShrink: 0 },
  barTrack : { flex: 1, height: 6, background: '#f3f4f6', borderRadius: 99, position: 'relative', overflow: 'hidden' },
  barCenter: { position: 'absolute', top: 0, bottom: 0, left: '50%', width: 1, background: '#e5e7eb' },
  barFill  : { position: 'absolute', top: 0, bottom: 0, borderRadius: 99, transition: 'all .3s ease' },
  barVal   : { fontSize: 11, fontWeight: 700, width: 44, textAlign: 'right', flexShrink: 0 },
  barUnit  : { fontSize: 10, color: '#9ca3af', width: 28, flexShrink: 0 },
  totalRow : { display: 'flex', alignItems: 'center', gap: 12, borderTop: '1px solid #f0f0ff', paddingTop: 12 },
  totalLabel: { fontSize: 11, fontWeight: 700, color: '#6b7280', flexShrink: 0 },
  totalBar : { flex: 1, height: 8, background: '#f3f4f6', borderRadius: 99, overflow: 'hidden' },
  totalFill: { height: '100%', borderRadius: 99, transition: 'all .3s ease', boxShadow: '0 0 8px rgba(99,102,241,0.4)' },
  totalVal : { fontSize: 13, fontWeight: 700, flexShrink: 0 },
};
